import React, { useState } from "react";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Chip,
  Collapse,
} from "@mui/material";
import {
  Dashboard,
  Groups,
  Support,
  AdminPanelSettings,
  Build,
  ExpandLess,
  ExpandMore,
  OpenInNew,
  SmartToy,
  Chat,
  Theaters,
  MusicNote,
  TheaterComedy,
  Event,
  CalendarMonth,
  BookOnline,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import AdminJson from "@/utilities/AdminJson";

interface User {
  id: number;
  email?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  isSuperuser?: boolean;
  isStaff?: boolean;
  hasUsablePassword?: string | boolean;
}

interface Team {
  id: number;
  name?: string;
  slug?: string;
}

interface AppNavMenuItemsProps {
  user: User | null;
  team: Team | null;
  activeTab?: string;
  onNavigate?: () => void; // For closing mobile drawers
}

export default function AppNavMenuItems({
  user,
  team,
  activeTab,
  onNavigate,
}: AppNavMenuItemsProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [superuserDropdownOpen, setSuperuserDropdownOpen] = useState(false); // Superuser dropdown state

  const handleNavigate = (path: string) => {
    navigate(path);
    onNavigate?.(); // Close mobile drawer if provided
  };

  // Enhanced active path detection
  const isActive = (path: string) => {
    const currentPath = location.pathname;

    // Use activeTab prop if provided (for server-side rendered pages)
    if (activeTab) {
      // Map activeTab values to paths
      const tabPathMap: Record<string, string[]> = {
        dashboard: ["/dashboard", "/a/"],
        "team-nav": ["/teams/", "/a/"],
        "ai-chat": ["/chat"],
        "group-chat": ["/group-chat"],
        profile: ["/profile", "/accounts/profile/"],
        "change-password": ["/accounts/password/change/"],
        "project-dashboard": ["/project-dashboard"],
        support: ["/support/"],
        admin: ["/admin/", "/superuser/"],
      };

      const paths = tabPathMap[activeTab] || [];
      return paths.some((tabPath) => path.startsWith(tabPath) && currentPath.startsWith(tabPath));
    }

    // Client-side path matching
    if (path === "/" && currentPath === "/") {
      return true;
    }

    // Handle team dashboard route
    if (
      team &&
      path === `/a/${team.slug}/` &&
      currentPath === `/a/${team.slug}/`
    ) {
      return true;
    }

    // Exact match for shorter paths to avoid false positives
    // e.g., "/teams/" should not match "/teams/settings/slug"
    if (path === "/teams/" && currentPath === "/teams/") {
      return true;
    }

    // For longer paths, use startsWith but ensure it's a proper path segment
    if (path !== "/" && path !== "/teams/" && currentPath.startsWith(path)) {
      // Ensure we match complete path segments, not partial matches
      const nextChar = currentPath[path.length];
      return (
        !nextChar || nextChar === "/" || nextChar === "?" || nextChar === "#"
      );
    }

    return false;
  };

  const NavItem = ({
    icon: Icon,
    text,
    path,
    badge,
    disabled = false,
    onClick,
    external = false,
    nested = false,
  }: {
    icon: React.ElementType;
    text: string;
    path?: string;
    badge?: string;
    disabled?: boolean;
    onClick?: () => void;
    external?: boolean;
    nested?: boolean;
  }) => {
    const active = path ? isActive(path) : false;

    return (
      <ListItem disablePadding>
        <ListItemButton
          onClick={() => {
            if (onClick) {
              onClick();
            } else if (path && !disabled) {
              if (external) {
                window.open(path, "_blank", "noopener,noreferrer");
              } else {
                handleNavigate(path);
              }
            }
          }}
          selected={active}
          disabled={disabled}
          sx={{
            pl: nested ? 5 : 3, // Extra indentation for nested items
            "&.Mui-selected": {
              backgroundColor: "rgba(25, 118, 210, 0.12)",
              "&:hover": {
                backgroundColor: "rgba(25, 118, 210, 0.16)",
              },
            },
            ...(active && {
              borderLeft: 3,
              borderLeftColor: "readable.link",
              backgroundColor: "rgba(25, 118, 210, 0.12)",
            }),
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <Icon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={text} />
          {external && (
            <OpenInNew fontSize="small" sx={{ ml: 1, opacity: 0.6 }} />
          )}
          {badge && (
            <Chip label={badge} size="small" color="secondary" sx={{ ml: 1 }} />
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  const SectionHeader = ({ title }: { title: string }) => (
    <ListItem>
      <ListItemText
        primary={
          <Typography
            variant="overline"
            color="text.secondary"
            fontWeight="bold"
            sx={{ fontSize: "0.75rem" }}
          >
            {title}
          </Typography>
        }
      />
    </ListItem>
  );

  // Don't render anything for anonymous users (matching Django template logic)
  if (!user || user.id === undefined) {
    return null;
  }

  return (
    <>
      <List sx={{ py: 0 }}>
        {/* Application Section */}
        <SectionHeader title="Application" />

        <NavItem
          icon={Dashboard}
          text="Dashboard"
          path={team ? `/a/${team.slug}/` : "/dashboard"}
        />

        <NavItem icon={Theaters} text="Venues" path="/venues/" />

        <NavItem icon={TheaterComedy} text="Stages" path="/stages/" />

        <NavItem icon={MusicNote} text="Performers" path="/performers/" />

        <NavItem icon={Event} text="Shows" path="/shows/" />

        <NavItem icon={CalendarMonth} text="Calendar" path="/shows/calendar/" />

        <NavItem icon={BookOnline} text="Holds" path="/bookings/" />

        <Divider sx={{ my: 1 }} />

        {/* Administration Section (only for superusers) */}
        {user.isSuperuser && (
          <>
            <Divider
              sx={{ my: 1, borderColor: "error.main", borderWidth: 1 }}
            />

            {/* Superuser Admin Collapsible Section */}
            <ListItem disablePadding>
              <ListItemButton
                onClick={() => setSuperuserDropdownOpen(!superuserDropdownOpen)}
                sx={{
                  pl: 3,
                  "&:hover": {
                    backgroundColor: "rgba(211, 47, 47, 0.08)",
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <AdminPanelSettings fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText
                  primary="Superuser Admin"
                  primaryTypographyProps={{
                    color: "statusText.attention",
                    fontWeight: "bold",
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                  }}
                />
                {superuserDropdownOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>

            <Collapse in={superuserDropdownOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <NavItem
                  icon={Dashboard}
                  text="Admin Dashboard"
                  path="/superuser/dashboard/"
                  nested={true}
                />

                <NavItem
                  icon={Groups}
                  text="User Management"
                  path="/superuser/users/"
                  nested={true}
                />

                {user.isStaff && <NavItem
                  icon={Support}
                  text="User Impersonation"
                  path="/support/"
                  nested={true}
                />}

                <NavItem
                  icon={Build}
                  text="Support Tools"
                  path="/superuser/support/"
                  nested={true}
                />

                <NavItem
                  icon={Dashboard}
                  text="Project Dashboard"
                  path="/project-dashboard"
                  external={false}
                  nested={true}
                />

                <NavItem
                  icon={AdminPanelSettings}
                  text="Django Admin"
                  path="/admin/"
                  external={true}
                  nested={true}
                />

                <NavItem
                  icon={Build}
                  text="API Swagger UI"
                  path="/api/schema/swagger-ui/"
                  external={true}
                  nested={true}
                />

                <NavItem
                  icon={AdminPanelSettings}
                  text="Admin Bridge"
                  path="/superuser/bridge/"
                  nested={true}
                />

                <NavItem
                  icon={Support}
                  text="Django Admin Docs"
                  path="/admin/doc/"
                  external={true}
                  nested={true}
                />

                {window.AI_CHAT_ENABLED !== false && (
                  <NavItem icon={SmartToy} text="AI Chat" path="/chat" nested />
                )}
                <NavItem icon={Chat} text="Group Chat" path="/group-chat" nested />
              </List>
            </Collapse>
          </>
        )}
      </List>
      <AdminJson
        isDebug={user.isSuperuser}
        componentName="AppNavMenuItems"
        data={{ user, team }}
      />
    </>
  );
}
